import React, { useEffect, useState } from "react";
import {
	Container,
	Typography,
	Box,
	Alert,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TextField,
	Button,
	IconButton,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
} from "@mui/material";
import { Edit, Link as LinkIcon } from "@mui/icons-material";
import { getItemsCustomer, updateItem } from "../services/api";

// Component for managing customer environments
const CustomerEnvironmentList = () => {
	const [items, setItems] = useState([]);
	const [companyId, setCompanyId] = useState(null);
	const [companyName, setCompanyName] = useState("");
	const [environments, setEnvironments] = useState([]);
	const [editingItem, setEditingItem] = useState(null);
	const [error, setError] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [testResult, setTestResult] = useState(null);
	const [testingUrl, setTestingUrl] = useState(false);
	const [openTestDialog, setOpenTestDialog] = useState(false);

	const token = localStorage.getItem("token");

	useEffect(() => {
		fetchItems();
	}, []);

	const fetchItems = async () => {
		try {
			const data = await getItemsCustomer(token);
			setCompanyId(data.companyId);
			setCompanyName(data.companyName);
			setEnvironments(data.environments || []);
			setItems(data.items || []);
		} catch (error) {
			setError("Erro ao buscar dados, faça login novamente");
			localStorage.removeItem("token");
			setTimeout(() => {
				window.location.href = "#/login";
			}, 2000);
		}
	};

	const handleEdit = (item) => {
		setEditingItem({ ...item });
	};

	const handleSave = async () => {
		if (!editingItem || !editingItem.AliasAccess) {
			setError("Erro: AliasAccess não encontrado!");
			return;
		}
		try {
			await updateItem(token, editingItem.AliasAccess, editingItem);
			setEditingItem(null);
			fetchItems();
		} catch {
			setError("Erro ao atualizar o item.");
		}
	};

	// Function to test URL connection
	const handleTestConnection = async (alias) => {
		const testUrl = `https://app.rental.itup.com.br/easyhub/default.php?cnpj=${alias}`;
		setTestingUrl(true);
		setOpenTestDialog(true);
		try {
			// Use GET instead of HEAD to retrieve the page content
			const response = await fetch(testUrl);
			const text = await response.text();
			console.log(text);
			// If the page content is blank or does not contain expected keywords, flag it as a failed connection.
			if (text.trim() === "") {
				setTestResult("❌ Falha na conexão: Página em branco!");
			} else if (text.toLowerCase().includes("/api/") || text.toLowerCase().includes("POST")) {
				// Adjust the keywords to those you expect in a successful response.
				setTestResult("✅ Conexão bem-sucedida!");
			} else {
				setTestResult("❌ Falha na conexão: Conteúdo inesperado!");
			}
		} catch (error) {
			console.error("Test connection error:", error);
			setTestResult("❌ Erro ao tentar conectar!");
		}
		setTestingUrl(false);
	};

	const filteredItems = items.filter((item) =>
		Object.values(item).some((value) => String(value).toLowerCase().includes(searchTerm.toLowerCase()))
	);

	return (
		<Container sx={{ mt: 4 }}>
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{/* Informações sobre Ambientes */}
			<Box sx={{ mt: 3 }}>
				<Typography variant="h6">🏢 Meus Ambientes</Typography>
				{companyId && (
					<Typography>
						Empresa vinculada: <b>{companyName}</b>
					</Typography>
				)}
				<Typography sx={{ mt: 1 }}>Ambientes acessíveis:</Typography>

				<TableContainer component={Paper} elevation={3} sx={{ mt: 2, maxHeight: 200 }}>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell>
									<b>Ambiente</b>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{environments.length > 0 ? (
								environments.map((env, index) => (
									<TableRow key={index}>
										<TableCell>{env}</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell>Nenhum ambiente encontrado</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>

			{/* Campo de busca */}
			<Box sx={{ mt: 4, mb: 2 }}>
				<TextField
					fullWidth
					variant="outlined"
					label="Buscar em itens..."
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
				/>
			</Box>

			{/* Tabela de Itens */}
			<TableContainer component={Paper} elevation={3} sx={{ maxHeight: 400 }}>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							{Object.keys(items[0] || {}).map((key) => (
								<TableCell key={key}>
									<b>{key}</b>
								</TableCell>
							))}
							<TableCell>
								<b>Ações</b>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredItems.map((item) => (
							<TableRow key={item.AliasAccess}>
								{Object.keys(item).map((key) => (
									<TableCell key={key}>
										{key === "Enable"
											? item[key] === "1"
												? "Habilitado"
												: "Desabilitado"
											: key === "Environment"
												? item[key] === "1"
													? "Produção"
													: "Teste"
												: item[key]}
										{/* If field is a URL, add test button */}
										{key.includes("UrlRest") && item[key] && (
											<IconButton
												color="primary"
												onClick={() => handleTestConnection(item["AliasAccess"])}
												sx={{ ml: 1 }}
											>
												<LinkIcon />
											</IconButton>
										)}
									</TableCell>
								))}
								<TableCell>
									<IconButton color="secondary" onClick={() => handleEdit(item)}>
										<Edit />
									</IconButton>
								</TableCell>
							</TableRow>
						))}
						{filteredItems.length === 0 && (
							<TableRow>
								<TableCell colSpan={Object.keys(items[0] || {}).length + 1}>Nenhum registro encontrado.</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Diálogo de Teste de Conexão */}
			<Dialog open={openTestDialog} onClose={() => setOpenTestDialog(false)}>
				<DialogTitle>Testando Conexão</DialogTitle>
				<DialogContent>{testingUrl ? <CircularProgress /> : <Typography>{testResult}</Typography>}</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenTestDialog(false)} color="primary">
						Fechar
					</Button>
				</DialogActions>
			</Dialog>

			{/* Diálogo para Edição de Item */}
			<Dialog open={Boolean(editingItem)} onClose={() => setEditingItem(null)}>
				<DialogTitle>Editar Ambiente</DialogTitle>
				<DialogContent>
					{editingItem &&
						Object.keys(editingItem).map((key) =>
							key !== "AliasAccess" && key !== "Descricao" && key !== "Enable" ? (
								<TextField
									key={key}
									label={key}
									fullWidth
									margin="dense"
									value={editingItem[key] || ""}
									onChange={(e) =>
										setEditingItem((prev) => ({
											...prev,
											[key]: e.target.value,
										}))
									}
								/>
							) : null
						)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleSave} color="primary">
						Salvar
					</Button>
					<Button onClick={() => setEditingItem(null)} color="secondary">
						Cancelar
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
};

export default CustomerEnvironmentList;
